import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postDocName } from '../../assets/nodeApi/postApi/postAdminApi';
import { putDocName } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchDocNameStatus } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { useGetDocumentName, useGetLmsGrades } from '../../assets/nodeApi/getApi/getAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';

const DocName = () => {
    const { showToaster } = useToaster();
    const [selectedDocName, setSelectedDocName] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const { data: grades, isLoading: isGradesLoading } = useGetLmsGrades();
    const { data: documents, isLoading: isDocumentsLoading } = useGetDocumentName();

    const gradeOptions = grades?.data?.map((item) => ({
        label: item.name,
        value: String(item.id),
        id: String(item.id),
    })) || [];

    const validationSchema = Yup.object().shape({
        gradeId: Yup.string().required('Grade is required'),
        docName: Yup.string().required('Document Name is required'),
    });

    const initialValues = {
        gradeId: selectedDocName?.gradeId ? String(selectedDocName.gradeId) : '',
        docName: selectedDocName?.docName || '',
    };

    const mutationAdd = useMutation({
        mutationFn: (payload) => postDocName(payload),
        onSuccess: () => {
            showToaster('Document created successfully!', '', 'success');
            queryClient.invalidateQueries({ queryKey: ['Document'] });
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to create document', '', 'error');
        },
    });

    const mutationUpdate = useMutation({
        mutationFn: ({ payload, id }) => putDocName(payload, id),
        onSuccess: () => {
            showToaster('Document updated successfully!', '', 'success');
            queryClient.invalidateQueries({ queryKey: ['Document'] });
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update document', '', 'error');
        },
    });

    const handleSubmit = (values, { resetForm }) => {
        if (isEditing) {
            mutationUpdate.mutate({
                payload: {
                    gradeId: Number(values.gradeId),
                    doc_name: values.docName
                },
                id: selectedDocName.id
            });
            queryClient.invalidateQueries({ queryKey: ['Document'] });

        } else {
            mutationAdd.mutate({
                gradeId: Number(values.gradeId),
                doc_name: values.docName
            });
            queryClient.invalidateQueries({ queryKey: ['Document'] });

            resetForm();
        }

        queryClient.invalidateQueries({ queryKey: ['Document'] });
        setIsEditing(false);
        setSelectedDocName(null);
    };

    const toggleStatusMutation = useMutation({
        mutationFn: ({ payload, id }) => patchDocNameStatus(id, payload),
        onSuccess: (response) => {
            showToaster(`Document ${response?.data?.status ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries({ queryKey: ['Document'] });
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update status', '', 'error');
        },
    });

    const docColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'docName', headerName: 'Document Name', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'grade_name', headerName: 'Grade', flex: 1, renderCell: (params) => <Typography>{params.row.grade?.name}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedDocName(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status}
                    onCheck={() =>
                        toggleStatusMutation.mutate({
                            payload: { status: !params.row.status ? true : false },
                            id: params.row.id
                        })
                    }
                />
            ),
        },
    ];

    const rowsDataWithSrNo = documents?.data?.map((item, index) => ({
        ...item,
        sr_no: index + 1
    })) || [];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Document' : 'Add Document'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="gradeId"
                                        component={IdAutoCompleteField}
                                        options={gradeOptions}
                                        isLoading={isGradesLoading}
                                        label="Grade"
                                        isRequired
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field name="docName"
                                        component={TextFormField}
                                        label="Document Name"
                                        isRequired
                                    />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedDocName(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>
            <CustomDivider />
            <TableFilterTemplate
                title="Document List"
                columns={docColumns}
                rowsData={rowsDataWithSrNo}
                isLoading={isDocumentsLoading} />
        </Box>
    );
};

export default DocName;



